import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useMutation } from 'react-query';
import * as api from '@hotjar/webapp/features/Settings/SitesAndOrganizations/AddOrganization/api';
import { useNotify } from '@hotjar/hotsauce';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { useOwnedAccount } from '@hotjar/account';
import { useOrganizationsInvalidate } from '@hotjar/webapp/features/Settings/SitesAndOrganizations/useOrganizationsInvalidate';
import type { AddOrganizationResponse } from '@hotjar/webapp/features/Settings/SitesAndOrganizations/AddOrganization/api';
import type { UseMutationResult } from 'react-query/types/react/types';
import { useInvalidatePermissionQuery } from '@hotjar/auth';

const TRANSLATION_DOMAIN = 'account:new-organization';

export const useAddOrganizationMutation = (
  onSuccess?: (orgId: number) => void,
): {
  mutation: UseMutationResult<AddOrganizationResponse, unknown, string>;
} => {
  const { add } = useTrack();
  const { showToast } = useNotify();
  const { t } = useTranslationWithPrefix(TRANSLATION_DOMAIN);
  const { data: ownedAccountData } = useOwnedAccount();
  const accountId = ownedAccountData?.id ?? null;
  const invalidate = useOrganizationsInvalidate();
  const invalidatePermissions = useInvalidatePermissionQuery();

  const mutation = useMutation(
    (name: string) => {
      if (accountId !== null) {
        return api.addOrganization(accountId, name);
      }
      return Promise.reject();
    },
    {
      onSuccess: (data) => {
        showToast({ text: t('save-success'), kind: 'success' });
        add('Organization');

        Promise.all([invalidatePermissions(), invalidate()]).then(() => {
          onSuccess?.(data.organization.id);
        });
      },
      onError: () => {
        showToast({ text: t('save-failure'), kind: 'danger' });
      },
    },
  );

  return { mutation };
};
